import { useEffect, useRef, useState } from "preact/hooks";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { ImportedChunk } from "../domain/ifcModelImport";

type Props = {
  chunks: readonly ImportedChunk[];
  elementIdByInstance: ReadonlyMap<number, string>;
  selectedElementId: string | null;
  onSelectElement: (id: string) => void;
};

/**
 * Extracts just the selected instance's triangles out of whichever chunks carry them, via each
 * chunk's precomputed `trianglesByInstance` — O(that instance's own triangles), not a rescan of
 * the whole model per click.
 */
function buildHighlightGeometry(chunks: readonly ImportedChunk[], instance: number): THREE.BufferGeometry | null {
  const positionsOut: number[] = [];
  for (const chunk of chunks) {
    const vertexIndices = chunk.trianglesByInstance.get(instance);
    if (!vertexIndices) continue;
    const position = chunk.geometry.getAttribute("position");
    for (const v of vertexIndices) positionsOut.push(position.getX(v), position.getY(v), position.getZ(v));
  }
  if (positionsOut.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positionsOut), 3));
  geometry.computeVertexNormals();
  return geometry;
}

// Ported from aec-project-controls/src/components/IfcModelViewport.tsx (same per-chunk design —
// see that file and domain/ifcModelImport.ts's header comment for why per-element mesh+material
// is not used). Camera auto-fits the imported model's bounding box (its scale is unknown ahead of
// time) and keeps the engine's own Z-up millimetre-derived world space (no axis remap). Renders
// one mesh PER CHUNK with a single shared material — not one mesh per element — because a real
// building can carry thousands of elements per chunk, and per-element mesh+material is what made
// a 432K-triangle real file (BLCJ Archicad sample) go slow with visible rendering artifacts in
// Controls' own history. Picking and the selected-element highlight both resolve from the
// chunk's own instanceByVertex buffer instead, and the highlight mesh is rebuilt only when the
// selection actually changes, not every frame.
export function IfcModelViewport({ chunks, elementIdByInstance, selectedElementId, onSelectElement }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const latestRef = useRef({ selectedElementId, onSelectElement });
  latestRef.current = { selectedElementId, onSelectElement };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#eef0ec");
    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100000);
    camera.up.set(0, 0, 1); // ifc-mesh-wasm's world space is Z-up, matching IFC itself.

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight("#ffffff", "#a3a89c", 3.0));
    const keyLight = new THREE.DirectionalLight("#fff8e8", 2.4);
    keyLight.position.set(3, -4, 8);
    scene.add(keyLight);

    // One shared material for every chunk mesh — the point of per-chunk rendering is exactly to
    // avoid a material (and a draw call) per element.
    const sharedMaterial = new THREE.MeshStandardMaterial({ color: "#c3c8bf", roughness: 0.8, metalness: 0.02, side: THREE.DoubleSide });
    const overallBox = new THREE.Box3();
    const chunkMeshes: THREE.Mesh[] = [];
    const chunkByMesh = new Map<THREE.Mesh, ImportedChunk>();
    for (const chunk of chunks) {
      const mesh = new THREE.Mesh(chunk.geometry, sharedMaterial);
      scene.add(mesh);
      chunkMeshes.push(mesh);
      chunkByMesh.set(mesh, chunk);
      chunk.geometry.computeBoundingBox();
      if (chunk.geometry.boundingBox) overallBox.union(chunk.geometry.boundingBox);
    }

    const instanceByElementId = new Map<string, number>();
    for (const [instance, elementId] of elementIdByInstance) instanceByElementId.set(elementId, instance);

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    if (!overallBox.isEmpty()) {
      overallBox.getCenter(center);
      overallBox.getSize(size);
    }
    const radius = Math.max(size.length() / 2, 0.5);
    camera.position.set(center.x + radius * 1.1, center.y - radius * 1.6, center.z + radius * 1.1);
    camera.lookAt(center);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(center);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    // minDistance/maxDistance default to 0/Infinity, so nothing stops the camera-target distance
    // shrinking toward zero on zoom in — and pan speed is proportional to that same distance, so
    // it degrades toward zero right along with it (the known Controls zoom/pan-degradation bug).
    controls.zoomToCursor = true;
    controls.minDistance = Math.max(radius * 0.05, 0.1);
    controls.maxDistance = radius * 25;
    controls.update();

    const grid = new THREE.GridHelper(radius * 6, 24, "#b7bab0", "#dcded6");
    grid.rotation.x = Math.PI / 2;
    grid.position.set(center.x, center.y, overallBox.min.z);
    scene.add(grid);

    let highlightMesh: THREE.Mesh | null = null;
    let highlightedElementId: string | null = null;
    const highlightMaterial = new THREE.MeshStandardMaterial({
      color: "#5f8fd1",
      emissive: "#274a7c",
      emissiveIntensity: 0.35,
      side: THREE.DoubleSide,
      depthTest: false,
    });

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let pointerDownAt: { x: number; y: number } | null = null;
    const onPointerDown = (event: PointerEvent) => {
      pointerDownAt = { x: event.clientX, y: event.clientY };
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!pointerDownAt) return;
      const moved = Math.hypot(event.clientX - pointerDownAt.x, event.clientY - pointerDownAt.y);
      pointerDownAt = null;
      if (moved > 4) return; // a drag (orbit), not a click
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(chunkMeshes, false)[0];
      if (!hit || hit.faceIndex === undefined || hit.faceIndex === null) return;
      const chunk = chunkByMesh.get(hit.object as THREE.Mesh);
      const index = chunk?.geometry.getIndex();
      if (!chunk || !index) return;
      // The first corner of the hit triangle decides — same convention ifc-engine-core's own
      // hierarchy index uses (every vertex of a triangle belongs to the same product).
      const vertexIndex = index.getX(hit.faceIndex * 3);
      const instance = chunk.instanceByVertex[vertexIndex];
      const elementId = elementIdByInstance.get(instance);
      if (elementId) latestRef.current.onSelectElement(elementId);
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointerup", onPointerUp);

    const resize = () => {
      const width = host.clientWidth;
      const height = host.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / Math.max(height, 1);
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    let animationFrame = 0;
    const render = () => {
      const state = latestRef.current;
      if (state.selectedElementId !== highlightedElementId) {
        highlightedElementId = state.selectedElementId;
        if (highlightMesh) {
          scene.remove(highlightMesh);
          highlightMesh.geometry.dispose();
          highlightMesh = null;
        }
        const instance = state.selectedElementId ? instanceByElementId.get(state.selectedElementId) : undefined;
        if (instance !== undefined) {
          const geometry = buildHighlightGeometry(chunks, instance);
          if (geometry) {
            highlightMesh = new THREE.Mesh(geometry, highlightMaterial);
            highlightMesh.renderOrder = 1;
            scene.add(highlightMesh);
          }
        }
      }
      controls.update();
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      controls.dispose();
      renderer.dispose();
      sharedMaterial.dispose();
      highlightMaterial.dispose();
      if (highlightMesh) (highlightMesh as THREE.Mesh).geometry.dispose();
      renderer.domElement.remove();
    };
  }, [chunks, elementIdByInstance]);

  if (webglUnavailable) {
    return (
      <div className="model-fallback" role="status">
        <strong>3D viewport unavailable</strong>
        <span>WebGL is not available in this browser.</span>
      </div>
    );
  }

  return (
    <div
      className="model-viewport"
      ref={hostRef}
      role="img"
      aria-label="Interactive 3D model imported from IFC. Drag to orbit, click an element to select it."
    />
  );
}
